import Auth from '@aws-amplify/auth';
import Storage from '@aws-amplify/storage';
import createDataProvider from './createDataProvider';
import sportsheadzApi from '../apis/sportsheadzApi';
import {
  ACCOUNT_DEACTIVATED_MESSAGE,
  API_ERROR_CODE_ENUM
} from '../config/constants';

const initialState = {
  email: '',
  password: '',
  isAuthenticated: false,
  isWaitingConfirmSignUp: false,
  userData: null,
  error: null,
  accountDeactivated: false,
  tokenAttempted: false,
  regAssociationAdmins: { value: [], loading: false, error: '' },
  loading: false
};

const isDeactivatedError = (err) =>
  err?.response?.data?.code === API_ERROR_CODE_ENUM.USER_DEACTIVATED;

// Cognito sign-in can succeed while the API rejects the session. Amplify's
// session then lives on in localStorage and sportsheadzApi keeps attaching its
// token, so tear it down before reporting the failure.
const signOutFailedApiSession = async (err) => {
  if (err?.isAxiosError) {
    try {
      await Auth.signOut();
    } catch {
      // best-effort; never swallow the user-facing message
    }
  }
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'fetching':
      return {
        ...state,
        loading: true,
        error: null,
        accountDeactivated: false
      };
    case 'success':
      return {
        ...state,
        error: null,
        accountDeactivated: false,
        loading: false,
        ...action.payload
      };
    case 'error':
      return {
        ...state,
        loading: false,
        accountDeactivated: false,
        error: action.payload
      };
    // accountDeactivated is only ever cleared by an explicit user action
    // (fetching / error / success), never by the implicit logout + reset that
    // LoginView and App run on mount — those would wipe it before it renders.
    case 'logout':
      return {
        ...initialState,
        tokenAttempted: true,
        accountDeactivated: state.accountDeactivated,
        ...action.payload
      };
    case 'reset': {
      return { ...initialState, accountDeactivated: state.accountDeactivated };
    }
    case 'set-association-admins':
      return {
        ...state,
        regAssociationAdmins: {
          ...state.regAssociationAdmins,
          ...action.payload
        }
      };
    default:
      return state;
  }
};

const fetchUserAdminData = async (dispatch) => {
  try {
    dispatch({
      type: 'set-association-admins',
      payload: { loading: true }
    });
    const response = await sportsheadzApi.get(`/api/me/registration-admins`, {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      },
      withCredentials: true
    });
    dispatch({
      type: 'success',
      payload: {
        regAssociationAdmins: {
          value: response.data.regAssociationAdmins,
          loading: false,
          error: null
        }
      }
    });
  } catch (error) {
    dispatch({
      type: 'set-association-admins',
      payload: {
        loading: false,
        error: 'Oops something went wrong.'
      }
    });
  }
};

const fetchRegAssociationAdminData = (dispatch) => async () => {
  fetchUserAdminData(dispatch);
};

const tokenSignin = (dispatch) => async () => {
  try {
    await Auth.currentAuthenticatedUser();
    const response = await sportsheadzApi.get('/api/user/auth', {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      },
      withCredentials: true
    });
    if (response.data.isRegistrationAdmin) {
      fetchUserAdminData(dispatch);
    }
    dispatch({
      type: 'success',
      payload: {
        isAuthenticated: true,
        tokenAttempted: true,
        userData: response.data
      }
    });
  } catch (err) {
    await signOutFailedApiSession(err);

    if (isDeactivatedError(err)) {
      dispatch({ type: 'logout', payload: { accountDeactivated: true } });
    }
    else {
      dispatch({ type: 'logout' });
    }
  }
};

const signin = (dispatch) => async (
  { email, password },
  onSuccessCallback,
  onErrorCallback
) => {
  if (email && password) {
    try {
      dispatch({ type: 'fetching' });
      await Auth.signIn(email, password);

      const response = await sportsheadzApi.get('/api/user/auth', {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      });
      if (response.data.isRegistrationAdmin) {
        fetchUserAdminData(dispatch);
      }

      dispatch({
        type: 'success',
        payload: {
          email,
          isAuthenticated: true,
          tokenAttempted: true,
          userData: response.data
        }
      });
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (err) {
      await signOutFailedApiSession(err);

      dispatch({ type: 'logout' });
      if (isDeactivatedError(err)) {
        dispatch({ type: 'error', payload: ACCOUNT_DEACTIVATED_MESSAGE });
      }
      else {
        switch (err.code) {
          case 'NotAuthorizedException':
          case 'UserNotFoundException':
            dispatch({
              type: 'error',
              payload: 'Incorrect username or password.'
            });
            break;
          case 'UserNotConfirmedException':
            dispatch({
              type: 'success',
              payload: { email, password, isWaitingConfirmSignUp: true }
            });
            break;
          default:
            dispatch({
              type: 'error',
              payload: 'An unknown error occured.'
            });
        }
      }
      if (onErrorCallback) {
        onErrorCallback();
      }
    }
  }
};

const createAccount = (dispatch) => async ({
  firstName,
  lastName,
  email,
  password
}) => {
  dispatch({ type: 'fetching' });
  try {
    await Auth.signUp({
      username: email,
      password,
      attributes: {
        given_name: firstName,
        family_name: lastName
      }
    });
    dispatch({
      type: 'success',
      payload: { email, password, isWaitingConfirmSignUp: true }
    });
  } catch (err) {
    let errorMessage;
    switch (err.code) {
      case 'UsernameExistsException':
        errorMessage = 'User with email already exists.';
        break;
      default:
        errorMessage = 'Oops something went wrong';
    }
    dispatch({ type: 'error', payload: errorMessage });
  }
};

const confirmAccount = (dispatch) => async (
  { email, confirmationCode },
  successCallback
) => {
  dispatch({ type: 'fetching' });
  try {
    await Auth.confirmSignUp(email, confirmationCode);
    dispatch({
      type: 'success',
      payload: { email, isWaitingConfirmSignUp: false }
    });

    if (successCallback) {
      successCallback();
    }
  } catch (err) {
    let errorMessage;
    switch (err.code) {
      case 'ExpiredCodeException':
        errorMessage = 'Code is expired. Are you already verified?';
        break;
      case 'LimitExceededException':
        errorMessage = 'Attempt limit exceeded, please try after some time.';
        break;
      case 'CodeMismatchException':
        errorMessage = 'Invalid verification code provided, please try again.';
        break;
      default:
        errorMessage = 'Oops something went wrong';
    }
    dispatch({ type: 'error', payload: errorMessage });
  }
};

const logout = (dispatch) => async () => {
  dispatch({ type: 'fetching' });
  await Auth.signOut();
  dispatch({ type: 'reset' });
};

const reset = (dispatch) => async () => {
  dispatch({ type: 'reset' });
};

const clearErrors = (dispatch) => async () => {
  dispatch({ type: 'error', payload: null });
};

const checkForExistingUser = (dispatch) => async (email, successCallback) => {
  dispatch({ type: 'fetching' });

  try {
    await Auth.confirmSignUp(email, '0');
  } catch (e) {
    if (e.code === 'UserNotFoundException') {
      dispatch({ type: 'error', payload: 'Oops something went wrong' });
    }
    else {
      successCallback();
      dispatch({
        type: 'success',
        payload: { email }
      });
    }
  }
};

const resendVerificationCode = (dispatch) => async (email, successCallback) => {
  dispatch({ type: 'fetching' });

  try {
    await Auth.resendSignUp(email);
    if (successCallback) {
      successCallback();
    }

    dispatch({
      type: 'success',
      payload: {
        email,
        isWaitingConfirmSignUp: true
      }
    });
  } catch (e) {
    dispatch({
      type: 'error',
      payload: 'Oops something went wrong. Are you already verified?'
    });
  }
};

const sendForgotPasswordConfirmation = (dispatch) => async (
  email,
  successCallback
) => {
  dispatch({ type: 'fetching' });

  try {
    await Auth.forgotPassword(email);
    dispatch({ type: 'success', payload: { email } });
    if (successCallback) {
      successCallback();
    }
  } catch (e) {
    if (e.code === 'InvalidParameterException') {
      dispatch({
        type: 'success',
        payload: {
          email,
          isWaitingConfirmSignUp: true
        }
      });
    }
    else {
      switch (e.code) {
        case 'LimitExceededException':
          dispatch({
            type: 'error',
            payload: 'Attempt limit exceeded, please try after some time.'
          });
          break;
        default:
          dispatch({ type: 'error', payload: 'Oops something went wrong.' });
      }
    }
  }
};

const resetPassword = (dispatch) => async (
  { email, code, newPassword },
  successCallback
) => {
  dispatch({ type: 'fetching' });

  try {
    await Auth.forgotPasswordSubmit(email, code, newPassword);
    dispatch({
      type: 'success',
      payload: { email }
    });
    if (successCallback) {
      successCallback();
    }
  } catch (e) {
    let errorMessage;
    switch (e.code) {
      case 'ExpiredCodeException':
        errorMessage = 'Code is expired. Are you already verified?';
        break;
      case 'LimitExceededException':
        errorMessage = 'Attempt limit exceeded, please try after some time.';
        break;
      case 'CodeMismatchException':
        errorMessage = 'Invalid verification code provided, please try again.';
        break;
      default:
        errorMessage = 'Error changing password';
    }
    dispatch({ type: 'error', payload: errorMessage });
  }
};

const changePassword = (dispatch) => async (
  { oldPassword, newPassword },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    let errorMessage;
    switch (e.code) {
      case 'NotAuthorizedException':
        errorMessage = 'Old password is invalid.';
        break;
      default:
        errorMessage = 'Error changing password';
    }
    if (onErrorCallback) {
      onErrorCallback(errorMessage);
    }
  }
};

const updateProfile = (dispatch) => async (
  { firstName, lastName, phone, avatar },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.put(
      `/api/me/profile`,
      { firstName, lastName, phone, avatar },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    const response = await sportsheadzApi.get('/api/user/auth', {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      },
      withCredentials: true
    });
    dispatch({
      type: 'success',
      payload: {
        isAuthenticated: true,
        tokenAttempted: true,
        userData: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    if (onErrorCallback) {
      onErrorCallback();
    }
  }
};

const updateAvatar = (dispatch) => async (
  { avatar },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.put(
      `/api/me/avatar`,
      { avatar },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    const response = await sportsheadzApi.get('/api/user/auth', {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      },
      withCredentials: true
    });
    dispatch({
      type: 'success',
      payload: {
        isAuthenticated: true,
        tokenAttempted: true,
        userData: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    if (onErrorCallback) {
      onErrorCallback();
    }
  }
};

export const { Provider, Context } = createDataProvider(
  authReducer,
  {
    tokenSignin,
    signin,
    logout,
    reset,
    createAccount,
    confirmAccount,
    clearErrors,
    checkForExistingUser,
    fetchRegAssociationAdminData,
    resendVerificationCode,
    sendForgotPasswordConfirmation,
    resetPassword,
    changePassword,
    updateProfile,
    updateAvatar
  },
  initialState
);
